"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  flaggedText,
  methodRows,
  ScoreWorking,
  TestWorking,
  TukeyWorking,
  type View,
  valueRows,
} from "@/components/tools/stats-descriptive-more/outlier-views";
import { StripPlot } from "@/components/tools/stats-descriptive-more/strip-plot";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import type { QuantileMethod } from "@/lib/stats/descriptive";
import {
  formatNumberList,
  formatStat,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type DixonLevel,
  flagRows,
  outlierReport,
} from "@/lib/tool-kit/stats-descriptive-more/outliers";

/** English strings. A translated content file overrides these through `ui`. */
export const OUTLIER_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder:
    "14.2, 15.1, 15.8, 16, 16.4, 16.9, 17.3, 17.5, 18.2, 19, 27.5, 31",
  valuesDetected: "one: value detected|other: values detected",
  methodLabel: "Quartile method (for Tukey's fences)",
  methodTukey: "Median of halves (TI-84, most textbooks)",
  methodInclusive: "Inclusive (Excel QUARTILE.INC, R type 7)",
  methodExclusive: "Exclusive (Excel QUARTILE.EXC, type 6)",
  kLabel: "Tukey fence multiplier (k)",
  kStandard: "1.5 — Tukey's standard fences",
  kFine: "2.2 — Hoaglin and Iglewicz (1987)",
  kExtreme: "3 — extreme outliers only",
  zLabel: "z-score cut-off",
  z2: "|z| > 2",
  z25: "|z| > 2.5",
  z3: "|z| > 3",
  alphaLabel: "Significance level for Grubbs and Dixon",
  alpha10: "α = 0.10 (90% confidence)",
  alpha05: "α = 0.05 (95% confidence)",
  alpha01: "α = 0.01 (99% confidence)",
  repeatGrubbs:
    "Repeat Grubbs' test after removing each outlier it finds (exploratory)",
  tryExample: "Try an example",
  trySmall: "Try a small sample",
  exampleData:
    "14.2, 15.1, 15.8, 16, 16.4, 16.9, 17.3, 17.5, 18.2, 19, 27.5, 31",
  exampleSmall: "4.1, 4.3, 4.4, 4.6, 4.7, 4.8, 5, 5.1, 6.9",
  clear: "Clear",
  enterPrompt: "Enter at least four numbers to check for outliers.",
  results: "Results",
  cardCount: "Values",
  cardAny: "Flagged by at least one method",
  cardTwo: "Flagged by two or more methods",
  plotTitle: "Dot plot",
  plotLabel:
    "Dot plot of {n} values from {min} to {max}; Tukey's fences at {lower} and {upper}; flagged values are enlarged.",
  plotNote:
    "Dashed lines are Tukey's fences; enlarged orange-red dots are flagged by at least one method.",
  fenceLegend: "Dashed lines: Tukey's fences",
  compareTitle: "What each method flags",
  colMethod: "Method",
  colRule: "Rule",
  colFlagged: "Flagged values",
  methodTukeyName: "Tukey's fences",
  methodZName: "z-score",
  methodModifiedName: "Modified z-score (MAD)",
  methodGrubbsName: "Grubbs' test",
  methodDixonName: "Dixon's Q test",
  ruleTukey: "Outside {lower} to {upper}",
  ruleZ: "|z| > {cutoff}",
  ruleModified: "|M| > {cutoff}",
  ruleGrubbs: "G > {critical} (α = {alpha})",
  ruleDixon: "Q > {critical} (α = {alpha})",
  none: "None",
  notAvailableZ: "Not available — the values have no spread.",
  notAvailableModified:
    "Not available — the median and mean absolute deviation are both zero.",
  notAvailableGrubbs:
    "Not available — needs at least three values with some spread.",
  notAvailableDixon:
    "Not available — Dixon's table covers {min} to {max} values with some spread.",
  valuesTitle: "Flagged values",
  colValue: "Value",
  colTimes: "Times",
  colTukey: "Tukey",
  colZ: "z-score",
  colModified: "Modified z",
  colGrubbs: "Grubbs",
  colDixon: "Dixon",
  flagged: "Flagged",
  notFlagged: "—",
  notApplicable: "n/a",
  noOutliers: "No method flags any value as an outlier.",
  workingTitle: "How each method was applied",
  sortedLine: "Sorted data (n = {n}): {list}",
  listSeparator: ", ",
  moreValues: "… and {count} more",
  tukeyQuartiles: "Q1 = {q1}, Q3 = {q3}, IQR = Q3 − Q1 = {iqr}",
  tukeyLower: "Lower fence = Q1 − {k} × IQR = {q1} − {k} × {iqr} = {fence}",
  tukeyUpper: "Upper fence = Q3 + {k} × IQR = {q3} + {k} × {iqr} = {fence}",
  tukeyExtreme:
    "Extreme outliers (beyond {lower} or {upper}, which is 3 × IQR from the quartiles): {list}",
  zMeanSd: "x̄ = {mean}, s = {sd} (sample standard deviation, n − 1)",
  zRule:
    "z = (x − x̄) ÷ s; flag when |z| > {cutoff}. Largest |z| in these data: {max}",
  zCeiling:
    "With {n} values no z-score can exceed (n − 1) ÷ √n = {ceiling}, so a cut-off of {cutoff} can never flag anything here.",
  modifiedMedian: "Median = {median}, MAD = median(|x − median|) = {mad}",
  modifiedRule:
    "M = 0.6745 × (x − median) ÷ MAD; flag when |M| > {cutoff}. Largest |M| in these data: {max}",
  modifiedMeanAd:
    "MAD is zero, so the scale is 1.253314 × MeanAD instead, as Iglewicz and Hoaglin prescribe: M = (x − median) ÷ (1.253314 × MeanAD). Largest |M| in these data: {max}",
  grubbsTableTitle: "Grubbs' test steps",
  colStep: "Step",
  colN: "n",
  colSuspect: "Suspect",
  colG: "G",
  colCritical: "Critical G",
  colP: "p-value",
  colResult: "Result",
  resultOutlier: "Outlier",
  resultKept: "Not an outlier",
  grubbsFormula:
    "G = max|x − x̄| ÷ s = |{suspect} − {mean}| ÷ {sd} = {g} (n = {n}, two-sided)",
  grubbsCritical:
    "Critical value = ((n − 1) ÷ √n) × √(t² ÷ (n − 2 + t²)) with t = {t}, the t quantile at 1 − α ÷ (2n) with n − 2 degrees of freedom → {critical}",
  grubbsPValue: "p {p}",
  dixonFormula:
    "Q = gap ÷ range = |{suspect} − {neighbour}| ÷ ({max} − {min}) = {q}",
  dixonCritical:
    "Critical Q for n = {n} at {confidence} confidence (Rorabacher, 1991) = {critical}",
  confidence90: "90%",
  confidence95: "95%",
  confidence99: "99%",
  dixonSignificant: "{value} is an outlier by Dixon's Q.",
  dixonKept: "No outlier by Dixon's Q.",
  cautionTitle: "Read with care",
  caution:
    "Grubbs' test assumes the data without the suspect value are roughly normal and tests one value at a time, so two similar outliers can hide each other (masking). Dixon's Q is for 3 to 10 values. The fences and the modified z-score make no normality assumption. A flagged value is a prompt to check the data, not a reason to delete it.",
  copySummary: "Copy summary",
  copied: "Copied!",
  summaryText:
    "n = {n}; flagged — Tukey: {tukey}; z-score: {z}; modified z: {modified}; Grubbs: {grubbs}; Dixon: {dixon}",
} as const;

type Strings = typeof OUTLIER_CALCULATOR_STRINGS;
type KChoice = "1.5" | "2.2" | "3";
type ZChoice = "2" | "2.5" | "3";
type AlphaChoice = "0.1" | "0.05" | "0.01";

const METHODS: readonly QuantileMethod[] = ["tukey", "inclusive", "exclusive"];

function methodLabel(method: QuantileMethod, strings: Strings): string {
  if (method === "inclusive") {
    return strings.methodInclusive;
  }
  if (method === "exclusive") {
    return strings.methodExclusive;
  }
  return strings.methodTukey;
}

export default function OutlierCalculatorWidget() {
  const strings = useToolUi(OUTLIER_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [method, setMethod] = useState<QuantileMethod>("tukey");
  const [kChoice, setKChoice] = useState<KChoice>("1.5");
  const [zChoice, setZChoice] = useState<ZChoice>("3");
  const [alphaChoice, setAlphaChoice] = useState<AlphaChoice>("0.05");
  const [repeat, setRepeat] = useState(false);

  const values = parseNumberList(raw);
  const alpha = Number(alphaChoice) as DixonLevel;
  const report =
    values.length >= 4
      ? outlierReport(values, {
          quartileMethod: method,
          tukeyK: Number(kChoice),
          zCutoff: Number(zChoice),
          alpha,
          repeatGrubbs: repeat,
        })
      : null;
  const num = (value: number) => formatStat(fmt, value);
  const view: View = {
    strings,
    fmt,
    num,
    list: (list) =>
      formatNumberList(fmt, list, strings.listSeparator, strings.moreValues),
  };
  const rows = report ? flagRows(report) : [];
  const flaggedSet = new Set(rows.map((row) => row.value));

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(values.length)}
              {tPlural(strings.valuesDetected, locale, values.length)}
            </>
          }
          id="oc-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="oc-k"
            label={strings.kLabel}
            onChange={setKChoice}
            options={[
              { value: "1.5", label: strings.kStandard },
              { value: "2.2", label: strings.kFine },
              { value: "3", label: strings.kExtreme },
            ]}
            value={kChoice}
          />
          <SelectField
            id="oc-z"
            label={strings.zLabel}
            onChange={setZChoice}
            options={[
              { value: "2", label: strings.z2 },
              { value: "2.5", label: strings.z25 },
              { value: "3", label: strings.z3 },
            ]}
            value={zChoice}
          />
          <SelectField
            id="oc-alpha"
            label={strings.alphaLabel}
            onChange={setAlphaChoice}
            options={[
              { value: "0.1", label: strings.alpha10 },
              { value: "0.05", label: strings.alpha05 },
              { value: "0.01", label: strings.alpha01 },
            ]}
            value={alphaChoice}
          />
        </div>
        <PillGroup
          label={strings.methodLabel}
          name="oc-method"
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: methodLabel(value, strings),
          }))}
          value={method}
        />
        <Checkbox
          checked={repeat}
          label={strings.repeatGrubbs}
          onChange={setRepeat}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleData)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleSmall)}
            type="button"
          >
            {strings.trySmall}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={raw === ""}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard label={strings.cardCount} value={fmt(report.n)} />
              <StatCard
                emphasis
                label={strings.cardAny}
                value={fmt(rows.length)}
              />
              <StatCard
                label={strings.cardTwo}
                value={fmt(rows.filter((row) => row.votes >= 2).length)}
              />
            </StatGrid>
            {rows.length === 0 ? (
              <Explanation>{strings.noOutliers}</Explanation>
            ) : null}
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
            <StripPlot
              data={report.data}
              fenceLabel={strings.fenceLegend}
              flagged={flaggedSet}
              fmt={fmt}
              label={t(strings.plotLabel, {
                n: fmt(report.n),
                min: num(report.data[0]),
                max: num(report.data[report.n - 1]),
                lower: num(report.tukey.lower),
                upper: num(report.tukey.upper),
              })}
              lowerFence={report.tukey.lower}
              upperFence={report.tukey.upper}
            />
            <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
            <ResultTable
              head={[strings.colMethod, strings.colRule, strings.colFlagged]}
              rows={methodRows(report, alpha, view)}
              wrapFirstColumn
            />
          </div>

          {rows.length > 0 ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.valuesTitle}</h3>
              <ResultTable
                head={[
                  strings.colValue,
                  strings.colTimes,
                  strings.colTukey,
                  strings.colZ,
                  strings.colModified,
                  strings.colGrubbs,
                  strings.colDixon,
                ]}
                rows={valueRows(report, rows, view)}
              />
            </div>
          ) : null}

          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={t(strings.summaryText, {
              n: fmt(report.n),
              tukey: flaggedText(report.tukey.flagged, view),
              z: report.z ? flaggedText(report.z.flagged, view) : "—",
              modified: report.modified
                ? flaggedText(report.modified.flagged, view)
                : "—",
              grubbs:
                report.grubbs.length > 0
                  ? flaggedText(report.grubbsFlagged, view)
                  : "—",
              dixon: report.dixon
                ? flaggedText(report.dixonFlagged, view)
                : "—",
            })}
          />

          <WorkingPanel title={strings.workingTitle}>
            <Formula>
              {t(strings.sortedLine, {
                n: fmt(report.n),
                list: view.list(report.data),
              })}
            </Formula>
            <TukeyWorking
              k={num(Number(kChoice))}
              report={report}
              view={view}
            />
            <ScoreWorking report={report} view={view} />
            <TestWorking alpha={alpha} report={report} view={view} />
          </WorkingPanel>

          <Notice>
            <strong>{strings.cautionTitle}.</strong> {strings.caution}
          </Notice>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
